/* eslint-disable react/require-default-props */
import React from 'react';
import {
  Box, SelectChangeEvent, CircularProgress, Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { IOption } from 'interfaces';

const CustomSelect = styled(Select)(({ theme }) => ({
  opacity: 1,
  backgroundColor: theme.palette.grey.A100,
  border: 0,
  borderRadius: '6px',
  fontSize: '18px',
  fontWeight: 500,
  outline: 'none',
  marginTop: '5px',
  height: '48px',
  width: '100%',
  color: theme.palette.text.secondary,
  '& .MuiOutlinedInput-input': {
    padding: '12px 14px',
    borderRadius: '6px',
  },
  '& .MuiSvgIcon-root': {
    color: theme.palette.text.secondary,
  },
  '& .MuiInputAdornment-root .MuiIconButton-root': {
    color: theme.palette.text.secondary,
  },
  '&.Mui-focused': {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
  },
  '&.Mui-focused .MuiOutlinedInput-input': {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.grey.A400,
    borderRadius: '6px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
    borderWidth: '1px',
  },
}));

interface PrimarySelectProps<T = unknown> {
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onChange?: (event: SelectChangeEvent<T>, child: React.ReactNode) => void;
  value?: string;
  name?: string;
  label?: string;
  innerLabel?: string;
  options: IOption[];
  placeholder?: string;
  loading?:boolean;
  className?:string;
  inlineLabel?:boolean;
  tabIndex?:number;
  disabled?:boolean;
  maxMenuHeight?: number;
}

const PrimarySelect = React.forwardRef(
  (
    {
      onChange, onBlur, value, name, label, innerLabel, options, placeholder, loading, tabIndex,
      className, inlineLabel, disabled, maxMenuHeight,
    }: PrimarySelectProps,
    ref,
  ) => (

    <Box data-testid="primary-select" className="select-wrap">
      { label && !inlineLabel && <Typography variant="body1">{label}</Typography> }
      {
      loading ? <div style={{ margin: 'auto' }}><CircularProgress size={20} /></div> : (
        <CustomSelect
          ref={ref}
          displayEmpty
          value={value}
          name={name}
          defaultValue={placeholder ? undefined : options[0].value}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          {...(maxMenuHeight && {
            MenuProps: {
              PaperProps: {
                style: {
                  maxHeight: maxMenuHeight,
                  overflowY: 'auto',
                },
              },
            },
          })}
          inputProps={{ 'aria-label': 'Without label' }}
          renderValue={(selected) => {
            // eslint-disable-next-line no-nested-ternary
            let selectedValue = placeholder as string;
            if ((selected as string)?.length) {
              const selectedOption = options.filter((option) => option.value === selected);
              if (selectedOption && selectedOption.length) {
                selectedValue = selectedOption[0].text as string;
              }
            }
            return (
              inlineLabel ? (
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    {`${label}:`}
                    &nbsp;
                  </Typography>
                  <Typography variant="body1">{selectedValue}</Typography>
                </Box>
              ) : selectedValue
            );
          }}
          className={className ?? undefined}
        >
          <MenuItem value="" disabled>
            {placeholder}
          </MenuItem>

          {options.map((option: any) => (
            <MenuItem key={option.id} disabled={option.disabled} value={option.value}>
              {option.text}
            </MenuItem>
          ))}
        </CustomSelect>
      )
}
    </Box>
  ),
);
export default PrimarySelect;
